/**
 * Video Matcher Utility
 * 
 * Centralized utility for determining if two videos/drills are the same.
 * This is the SINGLE SOURCE OF TRUTH for video comparison logic across the entire project.
 * 
 * Usage:
 *   - Deduplication: Are these two drills the same video?
 *   - Matching: Find a drill by ID or title
 *   - Validation: Compare expected vs actual drills
 */

/**
 * Extract YouTube video ID from URL
 * Handles formats:
 * - https://www.youtube.com/watch?v=Q8GrKwMG-Ck&list=...
 * - https://youtu.be/Q8GrKwMG-Ck
 * - Returns the ID after v=
 * 
 * This is the SINGLE SOURCE OF TRUTH for video ID extraction across the entire project.
 */
export const extractVideoId = (url: string): string | null => {
    if (!url || typeof url !== 'string') return null;
    
    try {
        // Handle youtube.com/watch?v= format
        const vMatch = url.match(/[?&]v=([^&]+)/);
        if (vMatch) {
            return vMatch[1];
        }
        
        // Handle youtu.be/ format
        const shortMatch = url.match(/youtu\.be\/([^?]+)/);
        if (shortMatch) {
            return shortMatch[1];
        }
        
        // If no match, return null
        return null;
    } catch (error) {
        return null;
    }
};

/**
 * Normalize a video ID by extracting it from URL if needed
 */
export const normalizeVideoId = (videoIdOrUrl: string | null | undefined): string | null => {
    if (!videoIdOrUrl) return null;
    
    // If it's already a video ID (no URL structure), return as-is
    if (!videoIdOrUrl.includes('youtube.com') && !videoIdOrUrl.includes('youtu.be')) {
        return videoIdOrUrl.trim();
    }
    
    // Extract from URL
    return extractVideoId(videoIdOrUrl);
};

/**
 * Normalize a drill name/title for comparison
 * - Lowercase
 * - Trim whitespace
 * - Remove extra spaces
 */
export const normalizeTitle = (title: string | null | undefined): string => {
    if (!title) return '';
    return title
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ');
};

/**
 * Get video ID from a drill object
 * Checks multiple possible fields: id, videoId, videoUrl
 */
export const getVideoIdFromDrill = (drill: any): string | null => {
    // Try direct video ID fields first
    if (drill.id) return normalizeVideoId(drill.id);
    if (drill.videoId) return normalizeVideoId(drill.videoId);
    
    // Try extracting from videoUrl
    if (drill.videoUrl) {
        const extracted = extractVideoId(drill.videoUrl);
        if (extracted) return extracted;
    }
    
    return null;
};

/**
 * Get title/name from a drill object
 */
export const getTitleFromDrill = (drill: any): string => {
    return drill.name || drill.title || drill.videoTitle || '';
};

/**
 * Check if two drills are the same video by ID
 * Returns true if both have the same normalized video ID
 */
export const areSameVideoById = (drill1: any, drill2: any): boolean => {
    const id1 = getVideoIdFromDrill(drill1);
    const id2 = getVideoIdFromDrill(drill2);
    
    if (!id1 || !id2) return false;
    
    return id1 === id2;
};

/**
 * Check if two drills have the same title (exact match after normalization)
 */
export const areSameVideoByTitle = (drill1: any, drill2: any): boolean => {
    const title1 = normalizeTitle(getTitleFromDrill(drill1));
    const title2 = normalizeTitle(getTitleFromDrill(drill2));
    
    if (!title1 || !title2) return false;
    
    return title1 === title2;
};

/**
 * Check if two drills are the same video (by ID or title)
 * This is the PRIMARY method for determining if drills are duplicates
 */
export const areSameVideo = (drill1: any, drill2: any): boolean => {
    // First check by video ID (most reliable)
    if (areSameVideoById(drill1, drill2)) {
        return true;
    }
    
    // Fall back to title match (for cases where same drill has different video IDs)
    return areSameVideoByTitle(drill1, drill2);
};

/**
 * Find a drill in an array by video ID
 * Returns the first matching drill or null
 */
export const findDrillById = (
    drills: any[],
    searchVideoId: string | null
): any | null => {
    if (!searchVideoId) return null;
    
    const normalizedSearchId = normalizeVideoId(searchVideoId);
    if (!normalizedSearchId) return null;
    
    for (const drill of drills) {
        const drillId = getVideoIdFromDrill(drill);
        if (drillId === normalizedSearchId) {
            return drill;
        }
    }
    
    return null;
};

/**
 * Find a drill in an array by title (exact match)
 * Returns the first matching drill or null
 */
export const findDrillByTitle = (
    drills: any[],
    searchTitle: string
): any | null => {
    if (!searchTitle) return null;
    
    const normalizedSearch = normalizeTitle(searchTitle);
    if (!normalizedSearch) return null;
    
    for (const drill of drills) {
        const drillTitle = normalizeTitle(getTitleFromDrill(drill));
        if (drillTitle === normalizedSearch) {
            return drill;
        }
    }
    
    return null;
};

/**
 * Find a drill in an array by title with fuzzy matching
 * Tries exact match first, then contains match, then reverse contains
 */
export const findDrillByTitleFuzzy = (
    drills: any[],
    searchTitle: string
): any | null => {
    if (!searchTitle) return null;
    
    const normalizedSearch = normalizeTitle(searchTitle);
    if (!normalizedSearch) return null;
    
    // Try exact match first
    const exactMatch = findDrillByTitle(drills, searchTitle);
    if (exactMatch) return exactMatch;
    
    // Try contains match
    for (const drill of drills) {
        const drillTitle = normalizeTitle(getTitleFromDrill(drill));
        if (drillTitle.includes(normalizedSearch) || normalizedSearch.includes(drillTitle)) {
            return drill;
        }
    }
    
    return null;
};

/**
 * Find a drill in an array by video ID or title (with fuzzy title matching)
 * This is the PRIMARY method for finding drills
 * Priority: 1) Video ID (exact), 2) Title (exact), 3) Title (fuzzy)
 */
export const findDrill = (
    drills: any[],
    searchTitle: string,
    searchVideoId: string | null = null
): any | null => {
    // Step 1: Try to match by video ID (most reliable)
    if (searchVideoId) {
        const matchById = findDrillById(drills, searchVideoId);
        if (matchById) return matchById;
    }
    
    // Step 2: Try exact title match
    if (searchTitle) {
        const matchByTitle = findDrillByTitle(drills, searchTitle);
        if (matchByTitle) return matchByTitle;
    }
    
    // Step 3: Try fuzzy title match
    if (searchTitle) {
        const matchByTitleFuzzy = findDrillByTitleFuzzy(drills, searchTitle);
        if (matchByTitleFuzzy) return matchByTitleFuzzy;
    }
    
    return null;
};

/**
 * Group drills by video ID
 * Returns a map of video ID -> array of drills with that ID
 */
export const groupDrillsById = (drills: any[]): Map<string, any[]> => {
    const grouped = new Map<string, any[]>();
    
    for (const drill of drills) {
        const videoId = getVideoIdFromDrill(drill);
        if (!videoId) continue;
        
        if (!grouped.has(videoId)) {
            grouped.set(videoId, []);
        }
        grouped.get(videoId)!.push(drill);
    }
    
    return grouped;
};

/**
 * Group drills by title
 * Returns a map of normalized title -> array of drills with that title
 */
export const groupDrillsByTitle = (drills: any[]): Map<string, any[]> => {
    const grouped = new Map<string, any[]>();
    
    for (const drill of drills) {
        const title = normalizeTitle(getTitleFromDrill(drill));
        if (!title) continue;
        
        if (!grouped.has(title)) {
            grouped.set(title, []);
        }
        grouped.get(title)!.push(drill);
    }
    
    return grouped;
};

